import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../components/DashboardLayout";
import axios from "../lib/api";
import { API } from "../App";
import { toast } from "sonner";
import {
  Send,
  LinkIcon,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  Globe,
  Bell,
  ExternalLink,
  Unlink,
  Info,
  ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const COMMON_TIMEZONES = [
  { label: "Madrid / Barcelona (CET/CEST)", value: "Europe/Madrid" },
  { label: "Ciudad de México (CST/CDT)", value: "America/Mexico_City" },
  { label: "Bogotá (COT)", value: "America/Bogota" },
  { label: "Buenos Aires (ART)", value: "America/Argentina/Buenos_Aires" },
  { label: "Lima (PET)", value: "America/Lima" },
  { label: "Santiago de Chile (CLT)", value: "America/Santiago" },
  { label: "Londres (GMT/BST)", value: "Europe/London" },
  { label: "Nueva York (ET)", value: "America/New_York" },
  { label: "Los Ángeles (PT)", value: "America/Los_Angeles" },
  { label: "UTC", value: "UTC" },
];

function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Madrid";
  } catch {
    return "Europe/Madrid";
  }
}

export default function TelegramNotificationsPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [notificationTime, setNotificationTime] = useState("08:00");
  const [timezone, setTimezone] = useState(detectBrowserTimezone());

  // Verification state
  const [deepLink, setDeepLink] = useState(null);
  const [polling, setPolling] = useState(false);

  const [codeExpiry, setCodeExpiry] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/telegram/config`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setConfig(res.data);
      setEnabled(res.data.enabled || false);
      setNotificationTime(res.data.notification_time || "08:00");
      setTimezone(res.data.timezone || detectBrowserTimezone());
    } catch {
      toast.error("Error cargando configuración de Telegram");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Poll for connection while deep link is active
  useEffect(() => {
    if (!deepLink || config?.connected) return;

    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${API}/telegram/config`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.data.connected) {
          setConfig(res.data);
          setDeepLink(null);
          setPolling(false);
          clearInterval(interval);
          toast.success("¡Telegram vinculado correctamente!");
        }
      } catch {
        /* silent */
      }
    }, 3000);

    // Stop polling after 15 min
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setPolling(false);
      setDeepLink(null);
    }, 15 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      setPolling(false);
    };
  }, [deepLink, config?.connected]);

  const handleGenerateCode = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/telegram/generate-code`, {}, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setDeepLink(res.data.deep_link);
      setCodeExpiry(res.data.expires_in_minutes);
      toast.success("Código generado — tienes 15 minutos para conectar");
    } catch {
      toast.error("Error generando el código de verificación");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!notificationTime.match(/^\d{2}:\d{2}$/)) {
      toast.error("Formato de hora inválido. Usa HH:MM");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/telegram/config`, {
        enabled,
        notification_time: notificationTime,
        timezone,
      }, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      toast.success("Configuración guardada");
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/telegram/test`, {}, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      toast.success("Mensaje de prueba enviado a tu Telegram");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando mensaje de prueba");
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("¿Deseas desvincular tu cuenta de Telegram?")) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/telegram/disconnect`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      toast.success("Telegram desvinculado");
      setDeepLink(null);
      await loadConfig();
    } catch {
      toast.error("Error desvinculando Telegram");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  const isConnected = config?.connected;
  const botUsername = config?.bot_username || "profitsystem_reports_bot";

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Notificaciones Telegram
          </h1>
          <p className="text-slate-500 mt-1">
            Recibe un resumen diario de tus KPIs directamente en Telegram.
          </p>
        </div>

        {/* Connection status card */}
        <div
          data-testid="telegram-connection-card"
          className={`rounded-xl border p-5 ${
            isConnected
              ? "bg-emerald-50 border-emerald-200"
              : "bg-white border-slate-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isConnected ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <XCircle className="w-6 h-6 text-slate-400" />
              )}
              <div>
                <p className="font-semibold text-slate-900">
                  {isConnected ? "Telegram vinculado" : "Telegram no vinculado"}
                </p>
                {isConnected && config.connected_at && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Conectado el{" "}
                    {new Date(config.connected_at).toLocaleDateString("es-ES")}
                  </p>
                )}
              </div>
            </div>
            {isConnected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                disabled={disconnecting}
                data-testid="telegram-disconnect-btn"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Unlink className="w-4 h-4 mr-1.5" />
                {disconnecting ? "Desvinculando..." : "Desvincular"}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleGenerateCode}
                disabled={generating}
                data-testid="telegram-connect-btn"
                className="bg-[#229ED9] hover:bg-[#1a8bc2] text-white"
              >
                <LinkIcon className="w-4 h-4 mr-1.5" />
                {generating ? "Generando..." : "Conectar Telegram"}
              </Button>
            )}
          </div>

          {/* Step-by-step connection instructions */}
          {!isConnected && !deepLink && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3">
                ¿Cómo funciona?
              </p>
              <ol className="space-y-2 text-sm text-slate-600">
                {[
                  'Pulsa "Conectar Telegram" para generar un código único',
                  `Se abrirá el bot @${botUsername} en Telegram`,
                  'Pulsa "Iniciar" en el bot para vincularlo automáticamente',
                  "Configura tu hora de notificación y guarda",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-xs flex items-center justify-center font-medium">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Deep link / verification step */}
          {deepLink && !isConnected && (
            <div
              data-testid="telegram-deeplink-section"
              className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3"
            >
              <div className="flex items-center gap-2 text-blue-800">
                {polling ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Info className="w-4 h-4" />
                )}
                <p className="text-sm font-medium">
                  {polling
                    ? "Esperando conexión..."
                    : `Código activo — válido ${codeExpiry} minutos`}
                </p>
              </div>
              <a
                href={deepLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="telegram-open-bot-link"
                className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-[#229ED9] hover:bg-[#1a8bc2] text-white rounded-lg text-sm font-medium transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Abrir @{botUsername} en Telegram
              </a>
              <p className="text-xs text-blue-600 text-center">
                Se vinculará automáticamente al pulsar "Iniciar" en el bot
              </p>
            </div>
          )}
        </div>

        {/* Configuration card */}
        <div
          data-testid="telegram-config-card"
          className="bg-white rounded-xl border border-slate-200 p-5 space-y-5"
        >
          <h2 className="font-semibold text-slate-900">
            Configuración de notificaciones
          </h2>

          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-800">Activar resumen diario</p>
              <p className="text-sm text-slate-500">
                Recibirás el resumen cada día a la hora configurada
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={!isConnected}
              data-testid="telegram-enabled-toggle"
            />
          </div>

          {/* Notification time */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-slate-400" />
              Hora de envío
            </label>
            <input
              type="time"
              value={notificationTime}
              onChange={(e) => setNotificationTime(e.target.value)}
              disabled={!isConnected}
              data-testid="telegram-time-input"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Timezone */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <Globe className="w-4 h-4 text-slate-400" />
              Zona horaria
              <span className="text-xs text-slate-400 font-normal">
                (detectada automáticamente)
              </span>
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!isConnected}
              data-testid="telegram-timezone-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
            >
              {/* Show current browser TZ if not in list */}
              {!COMMON_TIMEZONES.find((t) => t.value === timezone) && (
                <option value={timezone}>{timezone}</option>
              )}
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-1">
            <Button
              onClick={handleSave}
              disabled={saving || !isConnected}
              data-testid="telegram-save-btn"
              className="flex-1"
            >
              {saving ? "Guardando..." : "Guardar configuración"}
            </Button>
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || !isConnected}
              data-testid="telegram-test-btn"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {testing ? "Enviando..." : "Probar ahora"}
            </Button>
          </div>

          {!isConnected && (
            <p className="text-xs text-slate-400 text-center">
              Vincula tu Telegram primero para poder configurar las notificaciones
            </p>
          )}
        </div>

        {/* KPI preview card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-400" />
            Qué incluye el resumen diario
          </h2>
          <ul className="space-y-2 text-sm text-slate-600">
            {[
              "Ingresos del día anterior",
              "Número de pedidos del día anterior",
              "% Coste de publicidad sobre ventas",
              "Acumulado de ingresos del mes",
              "Previsión de cierre mensual (run rate)",
              "Comparativa vs mes anterior",
              "ROAS Google Ads (si conectado)",
              "ROAS Meta Ads (si conectado)",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          {config?.last_sent_at && (
            <p className="text-xs text-slate-400 mt-4">
              Último envío: {config.last_sent_at}
            </p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
